import React, {useState} from 'react';
import {Platform, ScrollView, View} from 'react-native';
import DatePicker from 'react-native-date-picker';
import Toast from 'react-native-simple-toast';
import {isNumber, noop} from 'lodash';

import Button from '/component/base/Button';
import FormField from '/component/base/FormField';
import Icon from '/component/base/Icon';
import Text from '/component/base/Text';
import Gutter from '/component/layout/Gutter';
import NavigationHeader from '/component/layout/NavigationHeader';
import {
  clearTestingCurrentPeriodId,
  clearTestingDate,
  clearTestingLastPeriodId,
  clearTestingLatestAcceptedPeriodId,
  clearTestingOpenPeriodId,
  setTestingCurrentPeriodId,
  setTestingDate,
  setTestingLastPeriodId,
  setTestingLatestAcceptedPeriodId,
  setTestingOpenPeriodId,
} from '/redux/api/storagePeriod';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {styles} from './TestingOptions.style';

const dateUtil = getDateUtilDefaultInstance();

// Define types for props
interface TestingOptionsProps {
  updateCurrentPeriodId: () => void;
  updateDate: () => void;
  updateLastPeriodId: () => void;
  updateLatestAcceptedPeriodId: () => void;
  updateOpenPeriodId: () => void;
  initialDate: number | Date;
  initialLastPeriodId: string;
  initialLatestAcceptedPeriodId: string;
  initialOpenPeriodId: string;
  initialPeriodId: string;
  clearFetchedData: () => void;
}

export const TestingOptions: React.FC<TestingOptionsProps> = ({
  updateCurrentPeriodId = noop,
  updateDate = noop,
  updateLastPeriodId = noop,
  updateLatestAcceptedPeriodId = noop,
  updateOpenPeriodId = noop,
  initialDate = dateUtil.getNow().getTime(),
  initialLastPeriodId = '',
  initialLatestAcceptedPeriodId = '',
  initialOpenPeriodId = '',
  initialPeriodId = '',
  clearFetchedData = noop,
}) => {
  const convertedInitialDate = !isNumber(initialDate)
    ? dateUtil.getNow()
    : new Date(initialDate);

  const [date, setDate] = useState<Date>(convertedInitialDate);
  const [openPeriodId, setOpenPeriodId] = useState<string>(initialLastPeriodId);
  const [currentPeriodId, setCurrentPeriodId] = useState<string>(
    initialLatestAcceptedPeriodId,
  );
  const [latestAcceptedPeriodId, setLatestAcceptedPeriodId] =
    useState<string>(initialOpenPeriodId);
  const [lastPeriodId, setLastPeriodId] = useState<string>(initialPeriodId);

  const saveToStorage = async () => {
    await Promise.all([
      setTestingCurrentPeriodId(currentPeriodId),
      setTestingDate(date.getTime()),
      setTestingLastPeriodId(lastPeriodId),
      setTestingLatestAcceptedPeriodId(latestAcceptedPeriodId),
      setTestingOpenPeriodId(openPeriodId),
    ]);
    updateCurrentPeriodId();
    updateDate();
    updateLastPeriodId();
    updateLatestAcceptedPeriodId();
    updateOpenPeriodId();

    clearFetchedData();

    Toast.show('saved');
  };

  const clearStorage = async () => {
    await Promise.all([
      clearTestingCurrentPeriodId(),
      clearTestingDate(),
      clearTestingLastPeriodId(),
      clearTestingLatestAcceptedPeriodId(),
      clearTestingOpenPeriodId(),
    ]);

    updateCurrentPeriodId();
    updateDate();
    updateLastPeriodId();
    updateLatestAcceptedPeriodId();
    updateOpenPeriodId();

    setDate(dateUtil.getNow());
    setOpenPeriodId('');
    setCurrentPeriodId('');
    setLatestAcceptedPeriodId('');
    setLastPeriodId('');

    clearFetchedData();

    Toast.show('cleared');
  };

  const onWebDateChange = (event: any) => {
    const newDate = new Date(event.target.value);
    if (!isNaN(newDate.getTime())) {
      setDate(newDate);
    }
  };

  const formattedDate = date.toISOString().split('T')[0];

  const formFieldStyle = (
    Platform.OS === 'web' ? (styles as any).formFieldRow : styles.formField
  ) as any;
  const formFieldIcon = Platform.OS === 'web' ? undefined : 'minus';

  return (
    <NavigationHeader style={{base: styles.base}}>
      <View style={styles.container}>
        <View style={styles.header}>
          <Gutter>
            <Text variant="large">Testing options</Text>
          </Gutter>
        </View>
        <ScrollView>
          <View style={styles.content}>
            <View style={styles.section}>
              <View style={styles.header}>
                <Gutter>
                  <Text variant="heading">Date options</Text>
                </Gutter>
              </View>
              <Gutter style={styles.warning}>
                <Icon name={'learn'} style={styles.icon} />
                <Text>
                  Please be aware that presentDate should be in the
                  currentPeriodId range of date. If presentDate is not included
                  in the range of dates in currentPeriodId then you may get the
                  wrong data in responses.
                </Text>
              </Gutter>
              <Gutter>
                <FormField
                  style={formFieldStyle}
                  icon={formFieldIcon}
                  label="openPeriodId"
                  onChangeText={setOpenPeriodId}
                  value={openPeriodId}
                  keyboardType="number-pad"
                />
                <FormField
                  style={formFieldStyle}
                  icon={formFieldIcon}
                  label="currentPeriodId"
                  onChangeText={setCurrentPeriodId}
                  value={currentPeriodId}
                  keyboardType="number-pad"
                />
                <FormField
                  style={formFieldStyle}
                  icon={formFieldIcon}
                  label="latestAcceptedPeriodId"
                  onChangeText={setLatestAcceptedPeriodId}
                  value={latestAcceptedPeriodId}
                  keyboardType="number-pad"
                />
                <FormField
                  style={formFieldStyle}
                  icon={formFieldIcon}
                  label="lastPeriodId"
                  onChangeText={setLastPeriodId}
                  value={lastPeriodId}
                  keyboardType="number-pad"
                />
              </Gutter>
              <View style={styles.datePicker}>
                {Platform.OS === 'web' ? (
                  <View style={styles.webDatePickerContainer as any}>
                    <input
                      type="date"
                      value={formattedDate}
                      onChange={onWebDateChange}
                      style={styles.webDatePicker as any}
                    />
                  </View>
                ) : (
                  <DatePicker
                    date={date}
                    onDateChange={setDate}
                    mode={'date'}
                  />
                )}
              </View>
              <View style={styles.buttons}>
                <Button style={{base: styles.button}} onPress={saveToStorage}>
                  Save
                </Button>
                <Button style={{base: styles.button}} onPress={clearStorage}>
                  Clear All
                </Button>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </NavigationHeader>
  );
};
